import { useEffect, useState } from 'react';
import { useGetUserDetailsMutation } from 'services/authApi';
import { setUser } from 'slice/authSlice';
import { useDispatch } from 'react-redux';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';

/**
 * A custom React hook for fetching and initializing user details after successful authentication.
 * It triggers a user details fetch operation based on the token initialization status. Upon
 * successfully fetching the user details, it dispatches an action to store these details in the
 * application's state using Redux. If the fetch operation fails, it sets an error state.
 *
 * @param {boolean} tokenInitialized A flag indicating whether the authentication token has been initialized.
 * @returns {object} An object containing the loading status (`userDetailsLoading`),
 * initialization status of user details (`userDetailsInitialized`), and any error state (`hasError`).
 */
function useUserDetails(tokenInitialized) {
  const dispatch = useDispatch();
  const [getUserDetails, { isLoading: userDetailsLoading }] = useGetUserDetailsMutation();
  const { refetch: refetchConf } = useConf(useGetConfigurationQuery);
  const [userDetailsInitialized, setUserDetailsInitialized] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (tokenInitialized) {
      getUserDetails()
        .unwrap()
        .then((payload) => {
          dispatch(setUser({ ...payload }));
          // Forces the configuration reloading
          refetchConf();

          setUserDetailsInitialized(true);
        })
        .catch((errors) => {
          console.error(`getUserDetails errors=${errors}`);
          setHasError(true);
        });
    }
  }, [tokenInitialized, getUserDetails, dispatch, refetchConf]);

  return { userDetailsLoading, userDetailsInitialized, hasError };
}

export default useUserDetails;
